/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import * as React from 'react';

import { IFindStoreWorldwideResources, ITermsOfServiceLinkData } from '../find-store-worldwide.props.autogenerated';

export interface IStoreSelectorEmptyMessageProps {
    resources: IFindStoreWorldwideResources;
}
export const StoreSelectorEmptyMessage: React.FC<IStoreSelectorEmptyMessageProps> = ({ resources }) => (
    <div className='ms-store-select-venchi__no-locations'>
        <p>{resources.emptyLocationsText}</p>
    </div>
);

export const StoreSelectorWaiting: React.FC = () => (
    <div className={classnames('msc-waiting', 'ms-store-select-venchi__search-in-progress')} />
);

export interface IStoreSelectorTermsOfServiceProps {
    link?: ITermsOfServiceLinkData;
    requestContext: Msdyn365.IRequestContext;
    onTextChange?(event: Msdyn365.ContentEditableEvent): void;
}

export const StoreSelectorTermsOfService: React.FC<IStoreSelectorTermsOfServiceProps> = ({ link, requestContext, onTextChange }) => {
    if (link) {
        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: link.ariaLabel,
            className: 'ms-store-select-venchi__terms-link',
            linkText: link.linkText,
            linkUrl: link.linkUrl.destinationUrl,
            openInNewTab: link.openInNewTab,
            role: 'link'
        };

        return (
            <div className='ms-store-select-venchi__footer'>
                <Msdyn365.Link link={editableLink} editProps={{ onTextChange, requestContext }} />
            </div>
        );
    }
    return null;
};

export interface IStoreSelectorFoundLocationsMessage {
    resources: IFindStoreWorldwideResources;
    locations?: IFullOrgUnitAvailability[];
    includeRetailers: boolean;
    forSearchBar: boolean;
    shouldDisplayLabel: boolean;
}
export const StoreSelectorFoundLocationsMessage: React.FC<IStoreSelectorFoundLocationsMessage> = ({
    locations,
    resources,
    includeRetailers,
    forSearchBar,
    shouldDisplayLabel
}) => {
    if (locations) {
        const countRetailers: number = locations.filter(loc => {
            return loc.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName?.startsWith('R ') ? true : false;
        }).length;
        const countVenchiStores: number = locations.length - countRetailers;
        return (
            <div
                className={
                    forSearchBar
                        ? 'ms-store-select-venchi__found-locations'
                        : `ms-store-select-venchi__found-locations-listmsg ${shouldDisplayLabel ? 'show' : 'hide'}`
                }
            >
                {includeRetailers
                    ? resources.storeAndRetailersAllCountMessage
                          .replace('{count}', countVenchiStores.toString())
                          .replace('{countRetailers}', countRetailers.toString())
                    : resources.storeAllCountMessage.replace('{count}', countVenchiStores.toString())}
            </div>
        );
    }

    return null;
};
